import React from "react";
import { displayPriceFormat, roundNumber } from "../../../utils";
import { useAppSelector } from "../../../redux/hook";
import {
  DISHOP_GREEN,
  MODIFY_ORDER_TYPE_PROMOTION,
  ORDER_TYPE_DELIVERY,
  PROMOTION_DELIVERY_FREE,
  PROMOTION_PERCENTAGE,
  PROMOTION_FREE_ORDER,
} from "../../../constants";
import OrderPromotion from "./OrderPromotion";
import Spinner from "../../Spinner";
import SkeletonRow from "../../Skeleton/SkeletonRow.view";
import { getTitleFromOffer } from "../../ThresholdMarkertingProgress/ThresholdMarketingProgress.service";
import DashedLine from "../../DashedLine/DashedLine";
import { Typography, Box } from "@mui/material";
import getStyles from "../Order.styles";
import { getColors } from "../../../utils";
import { getCartLineMarketingDiscountAmount } from "../../../utils/cartLineDiscount";

const getExplicitCartLineDiscountAmount = (cartItem: any): number => {
  if (!cartItem?.productMarketingCampaignKey) {
    return 0;
  }

  return getCartLineMarketingDiscountAmount(cartItem);
};

export const getDistributedMarketingDiscountAmount = (
  commande?: any,
): number => {
  if (!commande) {
    return 0;
  }

  return roundNumber(
    (Object.values(commande) as any[]).reduce(
      (sum: number, cartItem): number => {
        return sum + getExplicitCartLineDiscountAmount(cartItem);
      },
      0,
    ),
  );
};

// Derives the basket display contract from line data so the customer always sees
// a consistent (gross, remise, net) triple, even for pending orders that reach the
// webapp via Firebase Realtime and never pass through the backend's
// enrichOrderLikeWithCanonicalPricing. Mirrors the backend's display contract:
//   gross    = Σ line.price.fullPrice  (immune to the WEBAPP-net-value drift in
//              totalProductPriceOrder)
//   discount = max(marketingCampaignPromoAmount, distributedMarketingDiscountAmount)
//              — WEBAPP-written orders sometimes carry BNGM only at line level
//              (marketingCampaignPromoAmount=0) and sometimes carry the rolled-up
//              total; taking the max covers both without double-counting.
//   net      = max(0, gross - discount)
export const computeBasketDisplayContract = (
  commande?: any,
  marketingCampaignPromoAmount?: number,
): {
  displayBasketGrossPrice: number;
  displayBasketDiscountAmount: number;
  displayBasketNetPrice: number;
} | null => {
  if (!commande) {
    return null;
  }
  const gross = roundNumber(
    (Object.values(commande) as any[]).reduce((sum: number, item): number => {
      const lineGross =
        item?.price?.fullPrice ??
        item?.fullPrice ??
        (typeof item?.price === "number" ? item.price : 0);
      return sum + Number(lineGross || 0);
    }, 0),
  );
  const distributed = getDistributedMarketingDiscountAmount(commande);
  const orderLevelPromo = Number(marketingCampaignPromoAmount) || 0;
  const discount = roundNumber(Math.max(distributed, orderLevelPromo));
  const net = roundNumber(Math.max(0, gross - discount));
  return {
    displayBasketGrossPrice: gross,
    displayBasketDiscountAmount: discount,
    displayBasketNetPrice: net,
  };
};

export const getDiscountedTotals = ({
  totalPriceOrder,
  totalProductPriceOrder,
  marketingCampaignPromoAmount,
  distributedMarketingDiscountAmount = 0,
  displayBasketGrossPrice,
  displayBasketDiscountAmount,
  displayBasketNetPrice,
  feesAmount = 0,
  hasStoredOrderTotal = false,
}: {
  totalPriceOrder: number;
  totalProductPriceOrder: number;
  marketingCampaignPromoAmount?: number;
  distributedMarketingDiscountAmount?: number;
  displayBasketGrossPrice?: number;
  displayBasketDiscountAmount?: number;
  displayBasketNetPrice?: number;
  feesAmount?: number;
  hasStoredOrderTotal?: boolean;
}) => {
  const contractGross = Number(displayBasketGrossPrice);
  const contractDiscount = Number(displayBasketDiscountAmount);
  const contractNet = Number(displayBasketNetPrice);
  if (
    Number.isFinite(contractGross) &&
    Number.isFinite(contractDiscount) &&
    Number.isFinite(contractNet)
  ) {
    return {
      subtotalBeforeDiscount: contractGross,
      subtotalAfterDiscount: contractNet,
      totalAfterDiscount: totalPriceOrder,
      remainingMarketingDiscountAmount: contractDiscount,
    };
  }
  const reconciledNetPrice = (): number | null => {
    if (!hasStoredOrderTotal) {
      return null;
    }
    const charged = Number(totalPriceOrder);
    const fees = Number(feesAmount);
    if (!Number.isFinite(charged) || !Number.isFinite(fees)) {
      return null;
    }
    return Math.max(0, roundNumber(charged - fees));
  };

  const resolvedMarketingPromoAmount = Number(marketingCampaignPromoAmount);
  const resolvedProductTotal = Number(totalProductPriceOrder);
  const resolvedDistributedMarketingDiscountAmount = Number(
    distributedMarketingDiscountAmount,
  );
  const subtotalBeforeDiscount = Number.isFinite(resolvedProductTotal)
    ? resolvedProductTotal
    : 0;
  if (
    !Number.isFinite(resolvedMarketingPromoAmount) ||
    resolvedMarketingPromoAmount <= 0
  ) {
    const lineLevelDiscountAmount = Number.isFinite(
      resolvedDistributedMarketingDiscountAmount,
    )
      ? resolvedDistributedMarketingDiscountAmount
      : 0;

    const reconciledNet = reconciledNetPrice();

    if (lineLevelDiscountAmount > 0) {
      return {
        subtotalBeforeDiscount,
        subtotalAfterDiscount:
          reconciledNet ??
          Math.max(
            0,
            roundNumber(subtotalBeforeDiscount - lineLevelDiscountAmount),
          ),
        totalAfterDiscount: totalPriceOrder,
        remainingMarketingDiscountAmount: lineLevelDiscountAmount,
      };
    }

    return {
      subtotalBeforeDiscount,
      subtotalAfterDiscount: reconciledNet ?? subtotalBeforeDiscount,
      totalAfterDiscount: totalPriceOrder,
    };
  }

  const totalMarketingDiscountAmount = Math.max(
    0,
    roundNumber(resolvedMarketingPromoAmount),
  );
  const remainingMarketingDiscountAmount = Math.max(
    0,
    roundNumber(
      totalMarketingDiscountAmount -
        (Number.isFinite(resolvedDistributedMarketingDiscountAmount)
          ? resolvedDistributedMarketingDiscountAmount
          : 0),
    ),
  );

  return {
    subtotalBeforeDiscount,
    subtotalAfterDiscount:
      reconciledNetPrice() ??
      Math.max(
        0,
        roundNumber(subtotalBeforeDiscount - totalMarketingDiscountAmount),
      ),
    totalAfterDiscount: Math.max(
      0,
      roundNumber(totalPriceOrder - remainingMarketingDiscountAmount),
    ),
    remainingMarketingDiscountAmount,
  };
};

type OrderTotalProps = {
  commande?: any;
  promotion?: any;
  showFees: boolean;
  isLoading?: boolean;
  marketingCampaignPromoAmount?: number;
  totalPriceOrder: number;
  isOrderFreeDelivery?: boolean;
  managementFee: number;
  totalProductPriceOrder?: number;
  isPast?: boolean;
  displayBasketGrossPrice?: number;
  displayBasketDiscountAmount?: number;
  displayBasketNetPrice?: number;
};

export default function OrderTotal({
  commande,
  promotion,
  showFees,
  isLoading = false,
  marketingCampaignPromoAmount,
  totalPriceOrder,
  isOrderFreeDelivery = false,
  managementFee = 0,
  totalProductPriceOrder,
  isPast,
}: OrderTotalProps) {
  const {
    address = {},
    orderType,
    freeDelivery,
  } = useAppSelector((state) => state.pendingOrderReducer);

  const { thresholdOffer } = useAppSelector((state) => state.offerReducer);

  const userServicePrice = address?.deliveryPrice ?? 0;
  const styles = getStyles();

  const displayPriceAsync = (price: number) =>
    isLoading ? <Spinner /> : displayPriceFormat(price, false);
  const deliveryIsFree =
    (promotion && promotion.type === PROMOTION_DELIVERY_FREE) ||
    freeDelivery ||
    isOrderFreeDelivery;

  const isClickAndCollectPromo =
    promotion?.reconversionType ===
    MODIFY_ORDER_TYPE_PROMOTION.CLICK_AND_COLLECT;

  const hasPromotion =
    (promotion && !isClickAndCollectPromo) || !!marketingCampaignPromoAmount;
  const isDeliveryOrder = orderType === ORDER_TYPE_DELIVERY;

  const hasFee =
    (showFees && (isDeliveryOrder || managementFee > 0)) ||
    isClickAndCollectPromo;

  const chargedDeliveryPrice =
    (showFees && isDeliveryOrder) || isClickAndCollectPromo
      ? deliveryIsFree
        ? 0
        : userServicePrice
      : 0;
  const chargedManagementFee = showFees && managementFee > 0 ? managementFee : 0;
  const feesAmount = roundNumber(chargedDeliveryPrice + chargedManagementFee);

  const {
    subtotalBeforeDiscount,
    subtotalAfterDiscount,
    totalAfterDiscount,
    remainingMarketingDiscountAmount = 0,
  } = getDiscountedTotals({
    totalPriceOrder,
    totalProductPriceOrder,
    marketingCampaignPromoAmount,
    feesAmount,
    hasStoredOrderTotal: !!commande && Number.isFinite(Number(totalPriceOrder)),
  });

  const renderPromotion = (promo: any) => {
    if (!promo) return null;
    if (promo.type === PROMOTION_FREE_ORDER) {
      return (
        <OrderPromotion
          promotion={promo}
          commande={commande}
          promotionType=""
          prefix=""
          isLoading={isLoading}
        />
      );
    }

    const isPercentage =
      typeof promo.type === "string" &&
      promo.type.includes(PROMOTION_PERCENTAGE);

    const promoType =
      isPercentage && !isLoading
        ? promo.type
        : !isLoading && typeof promo.type === "number"
          ? displayPriceFormat(promo.type)
          : null;

    return (
      promoType && (
        <OrderPromotion
          promotion={promo}
          commande={commande}
          promotionType={promoType}
          prefix="-"
          isLoading={isLoading}
        />
      )
    );
  };

  return (
    <Box
      sx={{
        ...styles.orderTotalContainer,
        borderTop: isPast && getColors().border,
        padding: isPast && "16px",
      }}
    >
      <SkeletonRow
        label="Sous total"
        amount={displayPriceAsync(subtotalBeforeDiscount)}
        isLoading={isLoading}
        style={{ color: "unset" }}
      />
      {hasPromotion && (
        <>
          {promotion && !isClickAndCollectPromo && renderPromotion(promotion)}

          {!!remainingMarketingDiscountAmount && (
            <SkeletonRow
              label={
                <div style={{ lineHeight: 1.2 }}>
                  {commande ? "Remise" : getTitleFromOffer(thresholdOffer)}
                </div>
              }
              isLoading={isLoading}
              amount={`- ${displayPriceFormat(
                remainingMarketingDiscountAmount,
                false,
                true,
              )}`}
              style={{ color: DISHOP_GREEN }}
            />
          )}

          <SkeletonRow
            label="Sous total après remise"
            amount={displayPriceAsync(subtotalAfterDiscount)}
            isLoading={isLoading}
            style={{ color: "unset" }}
          />
          <DashedLine />
        </>
      )}

      {hasFee && (
        <>
          {((showFees && isDeliveryOrder) || isClickAndCollectPromo) && (
            <SkeletonRow
              label={<Typography variant="body1">Livraison</Typography>}
              amount={
                <Typography variant="subtitle1">
                  {displayPriceAsync(userServicePrice)}
                </Typography>
              }
              isLoading={isLoading}
              style={{ color: "unset" }}
            />
          )}

          {showFees && managementFee > 0 && (
            <SkeletonRow
              label={<Typography variant="body1">Frais et Services</Typography>}
              amount={
                <Typography variant="subtitle1">
                  {displayPriceAsync(managementFee)}
                </Typography>
              }
              isLoading={isLoading}
            />
          )}

          <DashedLine />
        </>
      )}

      {deliveryIsFree && promotion?.type !== PROMOTION_FREE_ORDER && (
        <>
          <OrderPromotion
            promotion={promotion}
            commande={commande}
            promotionType={displayPriceAsync(userServicePrice)}
            prefix="-"
            isLoading={isLoading}
            freeDelivery={deliveryIsFree}
          />
          <DashedLine />
        </>
      )}

      {promotion && isClickAndCollectPromo && (
        <>
          {renderPromotion(promotion)}
          <DashedLine />
        </>
      )}

      <Box sx={{ width: "100%", paddingTop: "5px" }}>
        <SkeletonRow
          label={
            <Typography variant="h5" sx={styles.capitalize}>
              Total TTC
            </Typography>
          }
          amount={
            <Typography variant="subtitle2">
              {displayPriceAsync(totalAfterDiscount)}
            </Typography>
          }
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
}
